import { Controller } from '@hotwired/stimulus';
import {
  ADDRESS_PARTS,
  Address,
  AutosuggestAddress,
} from '../../utilities/address';
import { debounce } from '../../utilities/debounce';

export default class extends Controller {
  private readonly SUBMIT_DEBOUNCE_TIME = 300;
  private readonly MINIMUM_CHAR_REQUEST = 5;

  static readonly targets = [
    'form',
    'autosuggestContainer',
    'autosuggestInput',
    'autosuggestMenu',
    'provinceField',
  ];

  static readonly classes = [
    'hintOption',
    'manualOption',
    'addressStreetNumberOption',
    'addressAddressNameOption',
    'addressSecondaryOption',
    'hidden',
    'menuButtonElement',
  ];

  static readonly values = {
    useAutosuggest: Boolean,
    hintLabel: String,
    manualInputLabel: String,
    searchUrl: String,
    address2InputField: String,
    cityInputField: String,
    provinceSelectField: String,
    zipInputField: String,
    countryInputField: String,
    animation: String,
    address1Autocomplete: String,
    isAddressComplete: Boolean,
    toggleElements: String,
  };

  declare autosuggestContainerTarget: HTMLDivElement;
  declare formTargets: HTMLDivElement[];
  declare autosuggestInputTarget: HTMLInputElement;
  declare autosuggestMenuTarget: HTMLMenuElement;
  declare provinceFieldTarget: HTMLInputElement | HTMLSelectElement;

  declare hintOptionClasses: string[];
  declare manualOptionClasses: string[];
  declare addressStreetNumberOptionClasses: string[];
  declare addressAddressNameOptionClasses: string[];
  declare addressSecondaryOptionClasses: string[];
  declare hiddenClasses: string[];
  declare menuButtonElementClasses: string[];

  declare useAutosuggestValue: boolean;
  declare isAddressCompleteValue: boolean;
  declare hintLabelValue: string;
  declare manualInputLabelValue: string;
  declare searchUrlValue: string;
  declare address2InputFieldValue: string;
  declare cityInputFieldValue: string;
  declare provinceSelectFieldValue: string;
  declare hasProvinceSelectFieldValue: boolean;
  declare zipInputFieldValue: string;
  declare countryInputFieldValue: string;
  declare address1AutocompleteValue: string;
  declare toggleElementsValue: string;

  declare animationValue: string;

  declare hasHintLabelValue: boolean;
  declare hasManualInputLabelValue: boolean;

  private suggestions: Address[] = [];
  private previousInputValue: string = '';
  private currentOption: number = 0;
  private initialIndex: number = 0;

  initialize(): void {
    if (this.useAutosuggestValue) {
      if (!this.isAddressCompleteValue) {
        this.hideForm();
      }
      this.cleanMenuOptions();
      this.initialIndex = this.hasHintLabelValue ? 1 : 0;
      this.currentOption = this.initialIndex;
    }
  }

  connect(): void {
    if (this.useAutosuggestValue) {
      this.autosuggestInputTarget.addEventListener(
        'focus',
        this.onAutosuggestInputFocus,
      );
      this.autosuggestContainerTarget.addEventListener(
        'focusout',
        this.onClickOutside,
      );
      this.autosuggestInputTarget.addEventListener(
        'input',
        debounce(this.onInput, this.SUBMIT_DEBOUNCE_TIME),
      );
      this.autosuggestInputTarget.addEventListener(
        'keydown',
        this.onInputKeyPressed,
      );
      this.autosuggestMenuTarget.addEventListener(
        'keydown',
        this.onMenuKeyPressed,
      );

      this.element.addEventListener('invalid', this.onManualInput, true);
    }
  }

  disconnect(): void {
    if (this.useAutosuggestValue) {
      this.autosuggestInputTarget.removeEventListener(
        'focus',
        this.onAutosuggestInputFocus,
      );
      this.autosuggestContainerTarget.removeEventListener(
        'click',
        this.onClickOutside,
      );
      this.autosuggestInputTarget.removeEventListener(
        'input',
        debounce(this.onInput, this.SUBMIT_DEBOUNCE_TIME),
      );
      this.autosuggestInputTarget.removeEventListener(
        'keydown',
        this.onMenuKeyPressed,
      );
      this.autosuggestMenuTarget.removeEventListener(
        'keydown',
        this.onMenuKeyPressed,
      );

      this.element.removeEventListener('invalid', this.onManualInput, true);
    }
  }

  formatZip(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const value = element.value;
    let valueStripped = value.replaceAll(/\D/g, '');

    if (valueStripped.length > 5) {
      valueStripped = [
        valueStripped.slice(0, 5),
        '-',
        valueStripped.slice(5),
      ].join('');
    }

    element.value = valueStripped;
  }

  fillFormElements(address: Address) {
    this.setAddressValue(address.address_1);
    this.saveActualInputValue();
    this.autosuggestInputTarget.dispatchEvent(
      new Event('change', { bubbles: true }),
    );
    this.setElementValueByName(this.address2InputFieldValue, address.address_2);
    this.setElementValueByName(this.cityInputFieldValue, address.city);
    if (this.hasProvinceSelectFieldValue) {
      this.setElementValueByName(
        this.provinceSelectFieldValue,
        address.province,
      );
    }

    this.setElementValueByName(this.zipInputFieldValue, address.postal_code);
    this.setElementValueByName(
      this.countryInputFieldValue,
      address.country_code,
    );
  }

  private onInputKeyPressed = (event: KeyboardEvent) => {
    const elementsQty = this.autosuggestMenuTarget.childNodes.length;
    switch (event.key) {
      case 'Escape': {
        this.hideMenu();
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        this.saveActualInputValue();
        this.currentOption = elementsQty - 1;
        this.doElementFocus(this.currentOption);

        break;
      }
      case 'ArrowDown': {
        event.preventDefault();
        this.saveActualInputValue();
        this.currentOption = this.initialIndex;
        this.doElementFocus(this.currentOption);

        break;
      }
      default: {
        break;
      }
    }
  };

  private onMenuKeyPressed = (event: KeyboardEvent) => {
    const elementsQty = this.autosuggestMenuTarget.childNodes.length;
    switch (event.key) {
      case 'Escape': {
        this.hideMenu();
        this.restorePreviousInputValue();
        this.focusOnAutosuggestInput();

        break;
      }
      case 'ArrowUp': {
        this.removeElementFocus(this.currentOption);
        this.currentOption =
          this.currentOption == this.initialIndex
            ? elementsQty - 1
            : this.currentOption - 1;
        this.doElementFocus(this.currentOption);

        break;
      }
      case 'ArrowDown': {
        this.removeElementFocus(this.currentOption);
        this.currentOption =
          this.currentOption == elementsQty - 1
            ? this.initialIndex
            : this.currentOption + 1;
        this.doElementFocus(this.currentOption);

        break;
      }
      default: {
        break;
      }
    }
  };

  private onManualInput = () => {
    this.hideMenu();
    this.showForm();
    this.focusOnForm();
  };

  private onAutosuggestInputFocus = () => {
    this.showMenu();
  };

  private onClickOutside = (event: Event) => {
    if (
      !this.autosuggestContainerTarget.contains(
        (event as MouseEvent).relatedTarget as Node,
      )
    ) {
      this.hideMenu();
      if (this.currentOption !== this.initialIndex) {
        this.restorePreviousInputValue();
      }
    }
  };

  private onInput = (event: Event) => {
    event.preventDefault();
    const userInput = (event.target as HTMLInputElement).value.trim();
    this.toggleMainAddressAutocompleteStatus(userInput);
    this.showMenu();
    this.cleanMenuOptions();
    if (userInput.length >= this.MINIMUM_CHAR_REQUEST) {
      this.getOptions(userInput);
    }
  };

  private toggleMainAddressAutocompleteStatus(value: string) {
    // Autocomplete "_off" is a workaround to suppress browser autocomplete
    const autocompleteValue =
      value.length > 0 ? '_off' : this.address1AutocompleteValue;
    this.autosuggestInputTarget.setAttribute('autocomplete', autocompleteValue);
  }

  private onSelectOption = (event: Event) => {
    const index = Number.parseInt(
      (event.currentTarget as HTMLButtonElement).value,
      10,
    );
    this.processOption(index);
  };

  private showMenu() {
    this.autosuggestMenuTarget.classList.remove(...this.hiddenClasses);
  }

  private hideMenu() {
    this.autosuggestMenuTarget.classList.add(...this.hiddenClasses);
  }

  private showForm() {
    for (const elementId of this.toggleElementsValue.split(',')) {
      document
        .querySelector(`[id="${elementId}"]`)
        ?.classList.remove(...this.hiddenClasses);
    }
    for (const form of this.formTargets) {
      form.classList.remove(...this.hiddenClasses);
    }
    this.setElementDisabledByName(
      this.element,
      this.address2InputFieldValue,
      false,
    );
    this.setElementDisabledByName(
      this.element,
      this.cityInputFieldValue,
      false,
    );
    this.setElementDisabledByName(
      this.element,
      this.provinceSelectFieldValue,
      false,
    );
    this.setElementDisabledByName(this.element, this.zipInputFieldValue, false);
  }

  private hideForm() {
    for (const elementId of this.toggleElementsValue.split(',')) {
      document
        .querySelector(`[id="${elementId}"]`)
        ?.classList.add(...this.hiddenClasses);
    }
    for (const form of this.formTargets) {
      form.classList.add(...this.hiddenClasses);
    }
    this.setElementDisabledByName(
      this.element,
      this.address2InputFieldValue,
      true,
    );
    this.setElementDisabledByName(this.element, this.cityInputFieldValue, true);
    this.setElementDisabledByName(
      this.element,
      this.provinceSelectFieldValue,
      true,
    );
    this.setElementDisabledByName(this.element, this.zipInputFieldValue, true);
  }

  private restorePreviousInputValue() {
    this.autosuggestInputTarget.value = this.previousInputValue;
  }

  private saveActualInputValue() {
    this.previousInputValue = this.autosuggestInputTarget.value;
  }

  private focusOnAutosuggestInput() {
    this.autosuggestInputTarget.focus();
  }

  private focusOnForm() {
    (
      this.element.querySelector(
        `[name="${this.address2InputFieldValue}"]`,
      ) as HTMLInputElement
    ).focus();
  }

  private removeElementFocus(optionIndex: number) {
    const element = this.autosuggestMenuTarget.childNodes[optionIndex];
    const button = element.childNodes[0] as HTMLButtonElement;
    button.setAttribute('tabindex', '-1');
  }

  private doElementFocus(optionIndex: number) {
    const element = this.autosuggestMenuTarget.childNodes[optionIndex];
    const button = element.childNodes[0] as HTMLButtonElement;
    const index = Number.parseInt(button.value, 10);
    if (index == 0) {
      this.setAddressValue(this.previousInputValue);
    } else {
      const address = this.suggestions[index - 1];
      if (address) {
        this.setAddressValue(this.buildAddress(address));
      }
    }
    button.setAttribute('tabindex', '0');
    button.focus();
  }

  private processOption(index: number) {
    const address = this.suggestions[index - 1];
    this.saveActualInputValue();
    this.hideMenu();
    this.cleanMenuOptions();
    if (address) {
      this.suggestions = [address];
      this.processMenuOptions([address]);
      this.fillFormElements(address);
    }
    this.showForm();
    this.focusOnForm();
  }

  private setAddressValue(address: string) {
    this.autosuggestInputTarget.value = address;
  }

  private buildAddress(address: Address) {
    return new AutosuggestAddress(address).parse([
      ADDRESS_PARTS.address_1,
      ADDRESS_PARTS.address_2,
      ADDRESS_PARTS.city,
      ADDRESS_PARTS.province,
      ADDRESS_PARTS.postal_code,
    ]);
  }

  private async getOptions(searchValue: string) {
    const url = `${this.searchUrlValue}?address=${searchValue}`;
    const response = await fetch(url);
    if (response && response.ok) {
      const data = await response.json();
      this.suggestions = data.data as Address[];
      this.processMenuOptions(this.suggestions);
    }
  }

  private cleanMenuOptions() {
    const menuChildNodes = this.autosuggestMenuTarget.childNodes;
    menuChildNodes[1]?.removeEventListener('click', this.onManualInput);
    for (
      let index = this.firstAutosuggestEntry();
      index < menuChildNodes.length;
      index++
    ) {
      menuChildNodes[index].removeEventListener('click', this.onSelectOption);
    }
    this.autosuggestMenuTarget.innerHTML = '';
    this.addDefaultMenuOptions();
    this.suggestions = [];
  }

  private firstAutosuggestEntry() {
    let count: number = this.hasHintLabelValue ? 1 : 0;
    count += this.hasManualInputLabelValue ? 1 : 0;

    return count;
  }

  private addDefaultMenuOptions() {
    if (this.hasHintLabelValue) {
      this.autosuggestMenuTarget.append(this.createHintMenuOption());
    }
    if (this.hasManualInputLabelValue) {
      this.autosuggestMenuTarget.append(this.createManualInputMenuOption());
    }
  }

  private processMenuOptions(suggestions: Address[]) {
    if (suggestions) {
      suggestions.map((address, index) => {
        this.autosuggestMenuTarget.append(
          address && this.createAddressElement(address, index + 1),
        );
      });
    }
  }

  private createAddressElement(address: Address, index: number) {
    const { address_1, address_2, city, postal_code, province } = address;
    const mainElement = document.createElement('li');

    const buttonElement = document.createElement('button');
    buttonElement.type = 'button';
    buttonElement.addEventListener('click', this.onSelectOption);
    buttonElement.classList.add(...this.menuButtonElementClasses);
    buttonElement.value = index.toString();
    buttonElement.setAttribute('tabindex', '-1');
    buttonElement.dataset.ddPrivacy = 'mask';

    const firstRow = document.createElement('div');
    const streetNumberElement = document.createElement('span');
    const addressNameElement = document.createElement('span');

    const addressParts = address_1.split(' ');
    const streetNumber = addressParts[0];
    addressParts.shift();
    let addressName = addressParts.join(' ');
    if (address_2) {
      addressName = [addressName, address_2].join(' - ');
    }

    streetNumberElement.innerHTML = streetNumber;
    streetNumberElement.classList.add(...this.addressStreetNumberOptionClasses);
    addressNameElement.innerHTML = addressName;
    addressNameElement.classList.add(...this.addressAddressNameOptionClasses);
    firstRow.append(streetNumberElement);
    firstRow.append(addressNameElement);

    const secondRow = document.createElement('span');
    secondRow.innerHTML = `${city} ${province} ${postal_code}`;
    secondRow.classList.add(...this.addressSecondaryOptionClasses);

    buttonElement.append(firstRow);
    buttonElement.append(secondRow);
    mainElement.append(buttonElement);

    return mainElement;
  }

  private createManualInputMenuOption() {
    const manualElement = document.createElement('li');
    const buttonElement = document.createElement('button');
    buttonElement.type = 'button';
    buttonElement.classList.add(...this.manualOptionClasses);
    buttonElement.innerHTML = this.manualInputLabelValue;
    buttonElement.addEventListener('click', this.onManualInput);
    manualElement.append(buttonElement);
    buttonElement.value = '0';
    buttonElement.setAttribute('tabindex', '-1');

    return manualElement;
  }

  private createHintMenuOption() {
    const hintElement = document.createElement('li');
    const buttonElement = document.createElement('button');
    buttonElement.classList.add(...this.hintOptionClasses);
    buttonElement.innerHTML = this.hintLabelValue;
    buttonElement.setAttribute('tabindex', '-1');
    hintElement.append(buttonElement);

    return hintElement;
  }

  private setElementValueByName(name: string, value: string) {
    if (name && value) {
      for (const element_ of document.getElementsByName(name)) {
        const element = element_ as HTMLInputElement | HTMLSelectElement;
        element.value = value;
        element.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  private setElementDisabledByName(
    element: Element,
    name: string,
    value: boolean,
  ) {
    if (element && name && value) {
      for (const element_ of element.querySelectorAll(name)) {
        const element = element_ as HTMLInputElement | HTMLSelectElement;
        element.disabled = value;
        element.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }
}
